import React, { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { PROPERTY_CHANNEL_MANAGERS } from '~/consts/reservation';

import { isSupportedChannelManager, useDetectMissingRates } from '~/hooks/apis/connectivity/useDetectMissingRates';

import ReservationService from '~/services/ReservationService';

import ChainRateDoctor from './ChainRateDoctor';

export default function RateDoctor() {
  const [scope, setScope] = useState<'property' | 'chain'>('property');
  const [propertyId, setPropertyId] = useState('');
  const [channelManager, setChannelManager] = useState<string | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);

  const detectMutation = useDetectMissingRates();

  const handleLookup = async () => {
    setChannelManager(null);
    setLookupError(null);
    setIsLookingUp(true);

    try {
      const response = await ReservationService.getProperty(propertyId.trim());
      const cm = response?.result?.channel_manager ?? null;
      if (!cm) {
        setLookupError('Property found but has no channel manager set.');
      } else {
        setChannelManager(cm);
      }
    } catch {
      setLookupError('Property not found. Check the ID and try again.');
    } finally {
      setIsLookingUp(false);
    }
  };

  const handleRun = () => {
    if (!channelManager || !isSupportedChannelManager(channelManager)) {
      return;
    }
    detectMutation.mutate(
      { propertyId: propertyId.trim(), channelManager },
      { onSuccess: () => detectMutation.reset() },
    );
  };

  const unsupported = channelManager !== null && !isSupportedChannelManager(channelManager);

  return (
    <Box maxWidth={480}>
      <ToggleButtonGroup
        value={scope}
        exclusive
        size="small"
        aria-label="Rate doctor scope"
        sx={{ mb: 3 }}
        onChange={(_event, value) => {
          if (value) {
            setScope(value);
          }
        }}
      >
        <ToggleButton value="property">Property</ToggleButton>
        <ToggleButton value="chain">Chain</ToggleButton>
      </ToggleButtonGroup>
      {scope === 'chain' ? (
        <ChainRateDoctor />
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Enter a property ID to enqueue a missing rates detection job for that property.
          </Typography>

          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                label="Property ID"
                value={propertyId}
                onChange={(e) => {
                  setPropertyId(e.target.value);
                  setChannelManager(null);
                  setLookupError(null);
                }}
                size="small"
                fullWidth
              />
              <Button
                variant="text"
                onClick={handleLookup}
                disabled={!propertyId.trim() || isLookingUp}
                sx={{ whiteSpace: 'nowrap', mt: '1px' }}
              >
                {isLookingUp ? <CircularProgress size={20} /> : 'Look up'}
              </Button>
            </Stack>

            {lookupError && <Alert severity="error">{lookupError}</Alert>}

            {channelManager && !unsupported && (
              <Alert severity="info">
                Channel manager: <strong>{channelManager}</strong>
              </Alert>
            )}

            {unsupported && (
              <Alert severity="warning">
                <strong>{channelManager}</strong> does not support missing rates detection. Only{' '}
                {PROPERTY_CHANNEL_MANAGERS.derbysoft} and {PROPERTY_CHANNEL_MANAGERS.synxis} are supported.
              </Alert>
            )}

            <Button
              variant="contained"
              onClick={handleRun}
              disabled={!channelManager || unsupported || detectMutation.isPending}
            >
              {detectMutation.isPending ? <CircularProgress size={20} color="inherit" /> : 'Run rate doctor'}
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
}
